import { lazy, Suspense, type JSX } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { RequireAuth } from './RequireAuth';

import { AppShell } from '@/components/layout/AppShell';
import { PageLoadingFallback } from '@/components/layout/PageLoadingFallback';
import { LoginPage } from '@/features/auth/LoginPage';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { TwoFactorSetupPage } from '@/features/settings/TwoFactorSetupPage';

/**
 * Découpage en chunks (Sprint 7 — réduction du bundle, voir PROGRESS.md) :
 * seules `LoginPage`, `DashboardPage` (point d'entrée universel de l'espace
 * authentifié — voir DashboardPage.tsx, qui charge elle-même sa section
 * lourde en graphiques à la demande) et `TwoFactorSetupPage` (déjà importée
 * statiquement par `LoginPage` pour le provisionnement forcé en cours de
 * connexion, voir plus bas) restent dans le chunk principal. Tout le reste
 * est chargé à la demande via `React.lazy`, chaque route enveloppant son
 * élément avec `<Suspense>` (voir `withSuspense` ci-dessous) plutôt qu'un
 * unique `<Suspense>` englobant : englober tout `<Routes>` ferait disparaître
 * l'ossature `AppShell` (en-tête + navigation) à chaque changement de page le
 * temps de charger le chunk suivant, alors qu'envelopper chaque `element`
 * individuellement ne fait disparaître que la zone de contenu sous
 * `<Outlet />`.
 */
const AbisReviewPage = lazy(() =>
  import('@/features/abis/AbisReviewPage').then((m) => ({ default: m.AbisReviewPage })),
);
const AuditPage = lazy(() => import('@/features/audit/AuditPage').then((m) => ({ default: m.AuditPage })));
const LotsCartePage = lazy(() =>
  import('@/features/cartes/LotsCartePage').then((m) => ({ default: m.LotsCartePage })),
);
const CarteTemplatesListPage = lazy(() =>
  import('@/features/carte-templates/CarteTemplatesListPage').then((m) => ({ default: m.CarteTemplatesListPage })),
);
const CarteTemplateEditorPage = lazy(() =>
  import('@/features/carte-templates/CarteTemplateEditorPage').then((m) => ({ default: m.CarteTemplateEditorPage })),
);
const ControleHistoriquePage = lazy(() =>
  import('@/features/controle/ControleHistoriquePage').then((m) => ({
    default: m.ControleHistoriquePage,
  })),
);
const ControlePage = lazy(() =>
  import('@/features/controle/ControlePage').then((m) => ({ default: m.ControlePage })),
);
const IncidentsPage = lazy(() =>
  import('@/features/logistique/IncidentsPage').then((m) => ({ default: m.IncidentsPage })),
);
const LotImpressionDetailPage = lazy(() =>
  import('@/features/impression/LotImpressionDetailPage').then((m) => ({
    default: m.LotImpressionDetailPage,
  })),
);
const LotsImpressionPage = lazy(() =>
  import('@/features/impression/LotsImpressionPage').then((m) => ({ default: m.LotsImpressionPage })),
);
const FileAttentePage = lazy(() =>
  import('@/features/file-attente/FileAttentePage').then((m) => ({ default: m.FileAttentePage })),
);
const SitesPage = lazy(() => import('@/features/sites/SitesPage').then((m) => ({ default: m.SitesPage })));
const MouvementsPage = lazy(() =>
  import('@/features/logistique/MouvementsPage').then((m) => ({ default: m.MouvementsPage })),
);
const StockDashboardPage = lazy(() =>
  import('@/features/logistique/StockDashboardPage').then((m) => ({ default: m.StockDashboardPage })),
);
const EmployeursListPage = lazy(() =>
  import('@/features/referentiel/EmployeursListPage').then((m) => ({ default: m.EmployeursListPage })),
);
const TravailleurDetailPage = lazy(() =>
  import('@/features/referentiel/TravailleurDetailPage').then((m) => ({
    default: m.TravailleurDetailPage,
  })),
);
const TravailleursListPage = lazy(() =>
  import('@/features/referentiel/TravailleursListPage').then((m) => ({
    default: m.TravailleursListPage,
  })),
);
const RolesPage = lazy(() => import('@/features/roles/RolesPage').then((m) => ({ default: m.RolesPage })));
const ChangePasswordPage = lazy(() =>
  import('@/features/settings/ChangePasswordPage').then((m) => ({ default: m.ChangePasswordPage })),
);
const HardwareSettingsPage = lazy(() =>
  import('@/features/settings/HardwareSettingsPage').then((m) => ({ default: m.HardwareSettingsPage })),
);
const UsersPage = lazy(() => import('@/features/users/UsersPage').then((m) => ({ default: m.UsersPage })));
const ZoneTestPage = lazy(() =>
  import('@/features/zone-test/ZoneTestPage').then((m) => ({ default: m.ZoneTestPage })),
);

/** Enveloppe un élément de route lazy avec le repli de chargement commun. */
function withSuspense(element: JSX.Element): JSX.Element {
  return <Suspense fallback={<PageLoadingFallback />}>{element}</Suspense>;
}

/**
 * Arborescence de routes. Chaque sprint ajoute ses routes protégées sous
 * <RequireAuth /> avec la permission adéquate.
 *
 * /changer-mot-de-passe et /provisionnement-2fa sont des écrans forcés (voir
 * RequireAuth) : montés sous <RequireAuth /> mais hors <AppShell/> (plein
 * écran, comme LoginPage) car ils doivent rester accessibles avant que la
 * session soit pleinement utilisable.
 */
export function AppRouter() {
  return (
    <Routes>
      <Route path="/connexion" element={<LoginPage />} />
      <Route path="/acces-refuse" element={<AccessDeniedPage />} />

      <Route element={<RequireAuth />}>
        <Route path="/changer-mot-de-passe" element={withSuspense(<ChangePasswordPage />)} />
        {/* Pas de `lazy()` ici : LoginPage importe déjà TwoFactorSetupPage
            statiquement pour le provisionnement forcé en cours de connexion
            (voir LoginPage.tsx) — elle finit de toute façon dans le chunk
            principal, un `lazy()` ici ne ferait que produire un avertissement
            de build ("dynamic import will not move module into another
            chunk") sans aucun gain réel. */}
        <Route path="/provisionnement-2fa" element={<TwoFactorSetupPage />} />

        <Route element={<AppShell />}>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/parametres/materiel" element={withSuspense(<HardwareSettingsPage />)} />

          <Route element={<RequireAuth permission="users.manage" />}>
            <Route path="/utilisateurs" element={withSuspense(<UsersPage />)} />
          </Route>
          <Route element={<RequireAuth permission="roles.manage" />}>
            <Route path="/roles" element={withSuspense(<RolesPage />)} />
          </Route>
          <Route element={<RequireAuth permission="audit.view" />}>
            <Route path="/audit" element={withSuspense(<AuditPage />)} />
          </Route>
          <Route element={<RequireAuth permission="referentiel.view" />}>
            <Route path="/referentiel/employeurs" element={withSuspense(<EmployeursListPage />)} />
            <Route path="/referentiel/travailleurs" element={withSuspense(<TravailleursListPage />)} />
            <Route path="/referentiel/travailleurs/:uuid" element={withSuspense(<TravailleurDetailPage />)} />
          </Route>
          <Route element={<RequireAuth permission="testzone.manage" />}>
            <Route path="/zone-test" element={withSuspense(<ZoneTestPage />)} />
          </Route>
          <Route element={<RequireAuth permission="biometrie.capture" />}>
            <Route path="/biometrie/file-attente" element={withSuspense(<FileAttentePage />)} />
          </Route>
          <Route element={<RequireAuth permission="abis.review" />}>
            <Route path="/abis/revue" element={withSuspense(<AbisReviewPage />)} />
          </Route>
          <Route element={<RequireAuth permission="carte.personnaliser" />}>
            <Route path="/cartes/lots" element={withSuspense(<LotsCartePage />)} />
            <Route path="/impression/lots" element={withSuspense(<LotsImpressionPage />)} />
            <Route path="/impression/lots/:uuid" element={withSuspense(<LotImpressionDetailPage />)} />
          </Route>
          <Route element={<RequireAuth permission="carte.design" />}>
            <Route path="/parametres/carte-modeles" element={withSuspense(<CarteTemplatesListPage />)} />
            <Route path="/parametres/carte-modeles/:uuid" element={withSuspense(<CarteTemplateEditorPage />)} />
          </Route>
          <Route element={<RequireAuth permission="sites.manage" />}>
            <Route path="/parametres/sites" element={withSuspense(<SitesPage />)} />
          </Route>
          <Route element={<RequireAuth permission="logistique.manage" />}>
            <Route path="/logistique/mouvements" element={withSuspense(<MouvementsPage />)} />
            <Route path="/logistique/stock" element={withSuspense(<StockDashboardPage />)} />
            <Route path="/logistique/incidents" element={withSuspense(<IncidentsPage />)} />
          </Route>
          <Route element={<RequireAuth permission="controle.effectuer" />}>
            <Route path="/controle" element={withSuspense(<ControlePage />)} />
            <Route path="/controle/historique" element={withSuspense(<ControleHistoriquePage />)} />
          </Route>
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AccessDeniedPage() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 text-center">
      <h1 className="text-xl font-semibold">Accès refusé</h1>
      <p className="text-muted-foreground">
        Votre profil ne dispose pas des droits nécessaires pour accéder à cette page.
      </p>
    </div>
  );
}
